import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import {itemsToExclude} from "../../utils/mappings";

const ResourceCalculator = ({ items }) => {
  // State initialization
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [targetExp, setTargetExp] = useState(1000);
  const [quantity, setQuantity] = useState(1);
  const [result, setResult] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (!isInitialized) return;
    
    const stateToSave = {
      selectedSkill,
      selectedTier,
      selectedIndustry,
      selectedCrop: selectedCrop ? {
        value: selectedCrop.value,
        label: selectedCrop.label,
        dataId: selectedCrop.data.id
      } : null,
      targetExp,
      quantity
    };
    
    localStorage.setItem('calculatorState', JSON.stringify(stateToSave));
  }, [selectedSkill, selectedTier, selectedIndustry, selectedCrop, targetExp, quantity, isInitialized]);

  // Load state from localStorage when component mounts and items are available
  useEffect(() => {
    if (!items || isInitialized) return;

    const savedState = localStorage.getItem('calculatorState');
    if (!savedState) {
      setIsInitialized(true);
      return;
    }

    try {
      const parsedState = JSON.parse(savedState);
      
      // Restore simple values
      if (parsedState.selectedSkill) setSelectedSkill(parsedState.selectedSkill);
      if (parsedState.selectedTier) setSelectedTier(parsedState.selectedTier);
      if (parsedState.selectedIndustry) setSelectedIndustry(parsedState.selectedIndustry);
      setTargetExp(parsedState.targetExp || 1000);
      setQuantity(parsedState.quantity || 1);

      // Restore crop selection
      if (parsedState.selectedCrop) {
        const matchingCrop = items.find(item => item.id === parsedState.selectedCrop.dataId);
        if (matchingCrop) {
          setSelectedCrop({
            value: matchingCrop.id,
            label: matchingCrop.name,
            data: matchingCrop
          });
        }
      }

      setIsInitialized(true);
    } catch (e) {
      console.error("Failed to load state", e);
      setIsInitialized(true);
    }
  }, [items, isInitialized]);

  function parseGrowTimeToMinutes(timeString) {
    let totalMinutes = 0;
  
    const hourMatch = timeString.match(/(\d+)\s*h/);
    const minMatch = timeString.match(/(\d+)\s*m/);
  
    if (hourMatch) {
      totalMinutes += parseInt(hourMatch[1], 10) * 60;
    }
  
    if (minMatch) {
      totalMinutes += parseInt(minMatch[1], 10);
    }
  
    return totalMinutes;
  }

  // Skill options
  const skillOptions = [
    { value: "farming", label: "Farming" },
    { value: "mining", label: "Mining" },
    { value: "forestry", label: "Forestry" },
    { value: "petcare", label: "Animal Care" },
    { value: "exploration", label: "Exploration" },
  ];

  // Get available tiers for mining/forestry
  const tierOptions = [1, 2, 3, 4].map((tier) => ({
    value: tier,
    label: `Tier ${tier}`,
  }));

  // Get available industries for petcare
  const industryOptions = items
    ? [
        ...new Set(
          items
            .filter((item) => item.produce?.some((p) => p.expType === "petcare" && p.exp !== null))
            .flatMap((item) => item.produce.filter((p) => p.expType === "petcare" && p.exp !== null).map((p) => p.industry))
        ),
      ]
        .filter(Boolean)
        .map((industry) => ({
          value: industry,
          label: industry,
        }))
    : [];

  //Get Land Options for Exploration
  const landOptions = items
    ? [
        ...new Set(
          items
            .filter((item) => item.produce?.some((p) => p.expType === "exploration" && p.exp !== null))
            .flatMap((item) => item.produce.filter((p) => p.expType === "exploration" && p.exp !== null).map((p) => p.industry))
        ),
      ]
        .filter(Boolean)
        .map((industry) => ({
          value: industry,
          label: industry,
        }))
    : [];

  // Get available crops for farming
  const cropOptions = items
    ? items
        .filter((item) => item.crop && !itemsToExclude.includes(item.id))
        .map((item) => ({
          value: item.id,
          label: item.name,
          data: item,
        }))
    : [];

  // Calculate requirements
  const calculate = useCallback(() => {
    if (!selectedSkill || !items) {
      setResult(null);
      return;
    }

    // Find highest EXP produce for selected tier (mining/forestry)
    const findBestTierItem = () => {
      if (!items || !selectedSkill || !selectedTier) return null;

      const validProduces = items
        .filter((item) => item.produce?.length > 0)
        .flatMap((item) =>
          item.produce
            .filter((p) => p.expType === selectedSkill.value && p.tier === selectedTier.value && p.exp !== null)
            .map((p) => ({
              ...p,
              itemName: item.name,
              itemImage: item.image,
            }))
        );

      if (validProduces.length === 0) return null;

      return validProduces.reduce((best, current) => (current.exp > best.exp ? current : best));
    };

    // Find lowest EXP produce for selected industry (petcare)
    const findIndustryItem = (skill) => {
      if (!items || !selectedIndustry || !skill) return null;

      const validProduces = items
        .filter((item) => item.produce?.length > 0)
        .flatMap((item) =>
          item.produce
            .filter((p) => skill === "petcare" ? p.expType === "petcare" : p.expType === "exploration" && p.industry === selectedIndustry.value && p.exp !== null)
            .map((p) => ({
              ...p,
              itemName: item.name,
              itemImage: item.image,
            }))
        );

      if (validProduces.length === 0) return null;

      return validProduces.reduce((best, current) => (current.exp < best.exp ? best : current));
    };

    let calculation = null;

    if (selectedSkill.value === "farming" && selectedCrop) {
      const crop = selectedCrop.data;
      const expPerHarvest = crop.crop.harvestExp || 0;
      const harvestsNeeded = Math.ceil(targetExp / expPerHarvest);
      const cyclesNeeded = Math.ceil(harvestsNeeded / quantity);
      const totalTime = (crop.crop.growTime * harvestsNeeded) / quantity / 60;
      const plantingEnergy = Math.abs(crop.crop.plantingEnergy) * harvestsNeeded;
      const harvestEnergy = crop.crop.harvestEnergy * harvestsNeeded;
      const totalEnergy = plantingEnergy + harvestEnergy;
      const energyCost = totalEnergy * 50;
      const expPerEnergy = (expPerHarvest * harvestsNeeded) / totalEnergy;

      calculation = {
        type: "Farming",
        item: crop.name,
        quantityUsed: quantity,
        harvestsNeeded,
        cyclesNeeded,
        totalTime: totalTime.toFixed(2),
        totalEnergy,
        energyCost,
        expPerEnergy: expPerEnergy.toFixed(2),
        expPerAction: expPerHarvest,
      };
    } else if ((selectedSkill.value === "mining" || selectedSkill.value === "forestry") && selectedTier) {
      const bestItem = findBestTierItem();

      if (bestItem) {
        const expPerAction = bestItem.exp;
        const actionsNeeded = Math.ceil(targetExp / expPerAction);
        const energyPerAction = bestItem.energy || 0;
        const rotations = Math.ceil(actionsNeeded / quantity);
        const totalEnergy = actionsNeeded * energyPerAction;
        const energyCost = totalEnergy * 50;
        const expPerEnergy = expPerAction / energyPerAction;
        const totalTime = ((parseGrowTimeToMinutes(bestItem.growTime) * actionsNeeded) / quantity) / 60;

        calculation = {
          type: selectedSkill.value === "mining" ? "Mining" : "forestry" ? "Forestry" : "Exploration",
          tier: `Tier ${bestItem.tier}`,
          quantityUsed: quantity,
          actionsNeeded,
          rotations,
          totalEnergy,
          energyCost,
          expPerEnergy: expPerEnergy.toFixed(2),
          expPerAction,
          totalTime,
        };
      }
    } else if ((selectedSkill.value === "petcare" || selectedSkill.value === "exploration") && selectedIndustry) {
      const bestItem = findIndustryItem(selectedSkill.value);

      if (bestItem) {
        const expPerAction = bestItem.exp;
        const actionsNeeded = Math.ceil(targetExp / expPerAction);
        const energyPerAction = bestItem.energy || 0;
        const totalEnergy = actionsNeeded * energyPerAction;
        const rotations = Math.ceil(actionsNeeded / quantity);
        const energyCost = totalEnergy * 50;
        const expPerEnergy = expPerAction / energyPerAction;
        const totalTime = ((parseGrowTimeToMinutes(bestItem.growTime) * actionsNeeded) / quantity) / 60;

        calculation = {
          type: selectedSkill.value === "petcare" ? "Animal Care" : "Exploration",
          industry: selectedIndustry.label,
          item: bestItem.itemName,
          quantityUsed: quantity,
          actionsNeeded,
          rotations,
          totalEnergy,
          energyCost,
          expPerEnergy: expPerEnergy.toFixed(2),
          expPerAction,
          totalTime
        };
      }
    }

    setResult(calculation);
  }, [selectedSkill, selectedTier, selectedIndustry, selectedCrop, targetExp, quantity, items]);

  // Reset dependent selections when skill changes
  useEffect(() => {
    if (selectedSkill) {
      setSelectedTier(null);
      setSelectedIndustry(null);
      setSelectedCrop(null);
      setResult(null);
    }
  }, [selectedSkill]);

  // Recalculate when any input changes
  useEffect(() => {
    calculate();
  }, [calculate]);

  return (
    <div className="calculator-style p-4 max-w-2xl mx-auto">
      <h2 className="calculator-title">Resource Production Calculator</h2>

      <div className="calculator-project rounded-lg shadow-md p-6 mb-6 space-y-4">
        <div className="form-group">
          <label className="block mb-2 font-medium">Production Skill</label>
          <Select options={skillOptions} value={selectedSkill} onChange={setSelectedSkill} placeholder="Select skill..." className="calculator-form" />
        </div>

        {selectedSkill?.value === "mining" || selectedSkill?.value === "forestry" ? (
          <div className="form-group">
            <label className="block mb-2 font-medium">Select Tier</label>
            <Select options={tierOptions} value={selectedTier} onChange={setSelectedTier} placeholder="Select tier..." className="calculator-form" />
          </div>
        ) : selectedSkill?.value === "petcare" ? (
          <div className="form-group">
            <label className="block mb-2 font-medium">Select Industry</label>
            <Select
              options={industryOptions}
              value={selectedIndustry}
              onChange={setSelectedIndustry}
              placeholder="Select industry..."
              isDisabled={!selectedSkill}
            />
          </div>
        ) : selectedSkill?.value === "exploration" ? (
          <div className="form-group">
            <label className="block mb-2 font-medium">Select Land Type</label>
            <Select
              options={landOptions}
              value={selectedIndustry}
              onChange={setSelectedIndustry}
              placeholder="Select Land Type..."
              isDisabled={!selectedSkill}
            />
          </div>
        ) : selectedSkill?.value === "farming" ? (
          <div className="form-group">
            <label className="block mb-2 font-medium">Select Crop</label>
            <Select
              options={cropOptions}
              value={selectedCrop}
              onChange={setSelectedCrop}
              placeholder="Select crop..."
              isDisabled={!selectedSkill}
              formatOptionLabel={(option) => (
                <div className="flex items-center gap-2 calculator-form">
                  <img src={option.data.image} alt={option.data.name} className="w-5 h-5" />
                  <span>
                    {option.data.name} (Lvl {option.data.crop.plantingLvl})
                  </span>
                </div>
              )}
            />
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div className="form-group">
            <label className="block mb-2 font-medium">Target EXP</label>
            <input
              type="number"
              min="1"
              value={targetExp}
              onChange={(e) => setTargetExp(parseInt(e.target.value) || 0)}
              className="calculator-form p-1 rounded"
            />
          </div>

          <div className="form-group">
            <label className="block mb-2 font-medium"># of Industries</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="calculator-form p-1 rounded"
            />
          </div>
        </div>
      </div>

      {result ? (
        <div className="bg-[var(--chart-bg)] rounded-lg shadow-md p-6 border">
          <h3 className="text-lg font-semibold mb-4">
            {result.type}
            {result.tier && <span className="ml-2 text-xs text-[var(--text3-color)]">{result.tier}</span>}
            {result.industry && <span className="ml-2 text-[var(--text3-color)]">({result.industry})</span>}
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="calc-card">
              <div className="text-sm text-[var(--text3-color)]">EXP Per Action</div>
              <div className="text-xl font-bold">{result.expPerAction}</div>
            </div>

            <div className="calc-card">
              <div className="text-sm text-[var(--text3-color)]">Actions Needed</div>
              <div className="text-xl font-bold">{result.actionsNeeded || result.harvestsNeeded}</div>
            </div>

            {result.cyclesNeeded && (
              <div className="calc-card">
                <div className="text-sm text-[var(--text3-color)]">Growth Cycles</div>
                <div className="text-xl font-bold">{result.cyclesNeeded}</div>
              </div>
            )}

            {result.rotations && (
              <div className="calc-card">
                <div className="text-sm text-[var(--text3-color)]">Rotations</div>
                <div className="text-xl font-bold">{result.rotations}</div>
              </div>
            )}

            {result.totalTime && (
              <div className="calc-card">
                <div className="text-sm text-[var(--text3-color)]">Total Time (hours)</div>
                <div className="text-xl font-bold">{result.totalTime}</div>
              </div>
            )}

            {result.item && (
              <div className="calc-card">
                <div className="text-sm text-[var(--text3-color)]">Item</div>
                <div className="text-xl font-bold">{result.item}</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 p-3 rounded border border-blue-100">
              <div className="text-sm text-blue-600">Total Energy</div>
              <div className="text-xl font-bold text-blue-800">{result.totalEnergy}</div>
            </div>

            <div className="bg-blue-50 p-3 rounded border border-blue-100">
              <div className="text-sm text-blue-600">Energy Cost (×50)</div>
              <div className="text-xl font-bold text-blue-800">{result.energyCost}</div>
            </div>

            <div className="bg-blue-50 p-3 rounded border border-blue-100">
              <div className="text-sm text-blue-600">EXP/Energy</div>
              <div className="text-xl font-bold text-blue-800">{result.expPerEnergy}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-6 border text-center text-gray-500">
          {selectedSkill ? "Select all required options to see calculations" : "Select a skill to begin"}
        </div>
      )}
    </div>
  );
};

export default ResourceCalculator;
